/**
 * Single source of truth for the legacy-origin tracking feature.
 *
 * The proxy (edge runtime), server-side cookie reader (auth layout +
 * banner), and client-side cookie reader (capture component) all import
 * from here so the whitelist + cookie name + query-param name + cookie
 * TTL stay in lockstep. Drift between these sites is silent — the
 * proxy could set a cookie that the layout's whitelist rejects, and
 * neither code review nor CI would catch it.
 *
 * Edge-runtime safe: no Node-only imports, no `next/headers`, no
 * Sentry. Pure constants + predicates.
 *
 * Background: see `auth-patterns` skill → "Cross-Domain Cookie Handoff
 * (RFC 6265)" for why we use a query-param handoff instead of setting
 * the cookie directly on the legacy 301 response.
 */

export const LEGACY_ORIGINS = ['ballee.app', 'yagp.app'] as const;
export type LegacyOrigin = (typeof LEGACY_ORIGINS)[number];

export const LEGACY_ORIGIN_COOKIE = 'legacy_origin';
export const LEGACY_ORIGIN_QUERY_PARAM = '_lo';
export const LEGACY_ORIGIN_COOKIE_MAX_AGE_SECONDS = 60 * 60 * 24 * 7;

const ORIGIN_SET = new Set<string>(LEGACY_ORIGINS);

/**
 * Type guard: is the value one of the whitelisted legacy hosts?
 *
 * Used at every read site (proxy handoff, capture component, banner,
 * auth layout) to defend against cookie tampering. A user setting
 * `document.cookie = 'legacy_origin=<script>...'` cannot inject into
 * Sentry tags, PostHog properties, or banner rendering because every
 * read site rejects values not in `LEGACY_ORIGINS`.
 */
export function isLegacyOrigin(
  value: string | null | undefined,
): value is LegacyOrigin {
  return !!value && ORIGIN_SET.has(value);
}

/**
 * Normalize a Host header to its canonical eTLD+1 form. Lowercases,
 * strips a leading `www.`, and drops a trailing `:port` suffix.
 *
 * Examples:
 *   - `Ballee.app`        → `ballee.app`
 *   - `www.ballee.app`    → `ballee.app`
 *   - `ballee.app:443`    → `ballee.app`
 *   - `WWW.YAGP.APP:8080` → `yagp.app`
 */
export function canonicalizeHost(host: string): string {
  return (
    host
      .toLowerCase()
      .replace(/^www\./, '')
      .split(':')[0] ?? ''
  );
}

/**
 * Apply the `legacy_origin` Sentry tag if the cookie carries a
 * whitelisted value. Pass `Sentry.setTag` (or a mock) as the second
 * argument so this is unit-testable without Sentry in the test
 * environment.
 */
export function applyLegacyOriginSentryTag(
  cookieValue: string | null | undefined,
  setTag: (key: string, value: string) => void,
): void {
  if (isLegacyOrigin(cookieValue)) {
    setTag(LEGACY_ORIGIN_COOKIE, cookieValue);
  }
}
